import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import Can from '../../components/Can';

// Tank-to-tank stock transfer. Pure stock movement (no money) — the backend
// records it in the Tank Ledger and re-derives current_stock for both tanks
// from the ledger (single source of truth). No manual stock editing here.
export default function TankTransfer() {
  const { t: tr } = useTranslation();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(today());
  const [form, setForm] = useState({ transfer_date: today(), from_tank_id: '', to_tank_id: '', qty: '', remarks: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };

  React.useEffect(() => {
    const wk = new Date(Date.now() - 30 * 86400 * 1000);
    setFrom(wk.toISOString().slice(0, 10));
  }, []);

  const { data: tanks } = useApi(() => api.stockLedger.tanks(), [], []);
  const { data, loading, error, reload } = useApi(
    () => (from && to ? api.tankTransfers.list(from, to) : Promise.resolve([])),
    [from, to],
    []
  );

  const tankById = (id) => (tanks || []).find(x => String(x.id) === String(id));
  const tankName = (id) => {
    const t = tankById(id);
    return t ? `${t.tank_no} (${t.product_code})` : id;
  };
  const tankLabel = (t) => {
    const stock = t.current_stock != null ? ` · ${fmtNum(t.current_stock)} L` : '';
    return `${t.tank_no} (${t.product_code})${stock}`;
  };

  // ── Live transfer preview / validation ────────────────────────────────
  const preview = useMemo(() => {
    const src = tankById(form.from_tank_id);
    const dst = tankById(form.to_tank_id);
    const qty = Number(form.qty);
    if (!src || !dst || !(qty > 0)) return null;

    const srcStock = Number(src.current_stock ?? NaN);
    const dstStock = Number(dst.current_stock ?? NaN);
    const afterSrc = Number.isFinite(srcStock) ? srcStock - qty : null;
    const afterDst = Number.isFinite(dstStock) ? dstStock + qty : null;
    const dstCap   = Number(dst.capacity || 0);

    const warnings = [];
    if (afterSrc != null && afterSrc < 0)
      warnings.push(tr('tankTransfer.warnings.exceedsStock', { tank: src.tank_no, stock: fmtNum(srcStock) }));
    if (afterDst != null && dstCap > 0 && afterDst > dstCap)
      warnings.push(tr('tankTransfer.warnings.exceedsCapacity', { tank: dst.tank_no, cap: fmtNum(dstCap) }));
    if (src.product_code !== dst.product_code)
      warnings.push(tr('tankTransfer.warnings.differentProducts', { from: src.product_code, to: dst.product_code }));

    return { src, dst, qty, afterSrc, afterDst, warnings, blocking: afterSrc != null && afterSrc < 0 };
  }, [form.from_tank_id, form.to_tank_id, form.qty, tanks]);

  // ── Range summary ─────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const rows = data || [];
    return { count: rows.length, totalQty: rows.reduce((s, r) => s + Number(r.qty || 0), 0) };
  }, [data]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.from_tank_id || !form.to_tank_id) return notify('❌ ' + tr('tankTransfer.errors.pickTanks'));
    if (String(form.from_tank_id) === String(form.to_tank_id)) return notify('❌ ' + tr('tankTransfer.errors.mustDiffer'));
    if (!(Number(form.qty) > 0)) return notify('❌ ' + tr('tankTransfer.errors.qtyPositive'));
    if (preview?.blocking) return notify('❌ ' + tr('tankTransfer.errors.exceedsStock'));
    setSaving(true);
    try {
      await api.tankTransfers.create({
        transfer_date: form.transfer_date,
        from_tank_id: Number(form.from_tank_id),
        to_tank_id: Number(form.to_tank_id),
        qty: Number(form.qty),
        remarks: form.remarks || null,
      });
      notify('✅ ' + tr('tankTransfer.toasts.recorded'));
      setForm({ transfer_date: today(), from_tank_id: '', to_tank_id: '', qty: '', remarks: '' });
      reload();
    } catch (err) { notify('❌ ' + err.message); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm(tr('tankTransfer.confirmDelete'))) return;
    try { await api.tankTransfers.remove(id); notify('✅ ' + tr('tankTransfer.toasts.deleted')); reload(); }
    catch (err) { notify('❌ ' + err.message); }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">{tr('tankTransfer.title')}</h2>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {tr('tankTransfer.subtitle')}
        </span>
      </div>

      {msg && <div style={{ marginBottom: 12, padding: '8px 12px', borderRadius: 6, background: 'var(--bg-muted, #f1f5f9)', color: 'var(--text-primary, #0f172a)', fontSize: 13 }}>{msg}</div>}
      {error && <ErrBanner msg={error} />}

      {/* ── Summary strip ──────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={kpi.tile}>
          <div style={kpi.label}>{tr('tankTransfer.kpiTransfers')}</div>
          <div style={kpi.value}>{summary.count}</div>
        </div>
        <div style={kpi.tile}>
          <div style={kpi.label}>{tr('tankTransfer.kpiVolume')}</div>
          <div style={{ ...kpi.value, color: 'var(--primary)' }}>{fmtNum(summary.totalQty)} L</div>
        </div>
        <div style={kpi.tile}>
          <div style={kpi.label}>{tr('tankTransfer.kpiActiveTanks')}</div>
          <div style={kpi.value}>{(tanks || []).length}</div>
        </div>
      </div>

      {/* ── New transfer form ──────────────────────────────────────── */}
      <Can module="stock_ledger" action="create">
        <div className="section-card" style={{ marginBottom: 16 }}>
          <div className="section-header">{tr('tankTransfer.newTransfer')}</div>
          <form onSubmit={submit} style={{ padding: 16 }}>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="form-group">
                <label>{tr('common.date')}</label>
                <input type="date" value={form.transfer_date}
                       onChange={e => setForm({ ...form, transfer_date: e.target.value })} required />
              </div>
              <div className="form-group" style={{ minWidth: 200 }}>
                <label>{tr('tankTransfer.fromTank')}</label>
                <select value={form.from_tank_id} onChange={e => setForm({ ...form, from_tank_id: e.target.value })} required>
                  <option value="">{tr('tankTransfer.selectOption')}</option>
                  {(tanks || []).map(t => <option key={t.id} value={t.id}>{tankLabel(t)}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ minWidth: 200 }}>
                <label>{tr('tankTransfer.toTank')}</label>
                <select value={form.to_tank_id} onChange={e => setForm({ ...form, to_tank_id: e.target.value })} required>
                  <option value="">{tr('tankTransfer.selectOption')}</option>
                  {(tanks || []).map(t => <option key={t.id} value={t.id}>{tankLabel(t)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>{tr('tankTransfer.quantityL')}</label>
                <input type="number" step="0.001" min="0" value={form.qty}
                       onChange={e => setForm({ ...form, qty: e.target.value })} required />
              </div>
              <div className="form-group" style={{ flex: 1, minWidth: 160 }}>
                <label>{tr('common.remarks')}</label>
                <input type="text" value={form.remarks}
                       onChange={e => setForm({ ...form, remarks: e.target.value })} placeholder={tr('common.optional')} />
              </div>
              <button className="btn btn-primary" disabled={saving || preview?.blocking}>
                {saving ? tr('common.saving') : tr('tankTransfer.addTransfer')}
              </button>
            </div>

            {/* Live preview + warnings */}
            {preview && (
              <div style={{
                marginTop: 14, padding: '10px 14px', borderRadius: 8,
                background: 'var(--bg-muted, #f1f5f9)', fontSize: 13,
                display: 'flex', flexDirection: 'column', gap: 6,
              }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.4 }}>
                    {tr('tankTransfer.afterTransfer')}
                  </span>
                  <span>
                    <strong>{preview.src.tank_no}</strong>{' '}
                    {preview.afterSrc != null
                      ? <span style={{ fontFamily: 'monospace', color: preview.afterSrc < 0 ? 'var(--danger)' : 'var(--text-primary)' }}>→ {fmtNum(preview.afterSrc)} L</span>
                      : <span style={{ color: 'var(--text-muted)' }}>→ −{fmtNum(preview.qty)} L</span>}
                  </span>
                  <span style={{ color: 'var(--text-muted)' }}>·</span>
                  <span>
                    <strong>{preview.dst.tank_no}</strong>{' '}
                    {preview.afterDst != null
                      ? <span style={{ fontFamily: 'monospace', color: 'var(--secondary, #059669)' }}>→ {fmtNum(preview.afterDst)} L</span>
                      : <span style={{ color: 'var(--text-muted)' }}>→ +{fmtNum(preview.qty)} L</span>}
                  </span>
                </div>
                {preview.warnings.map((w, i) => (
                  <div key={i} style={{ color: 'var(--danger)', fontSize: 12 }}>⚠️ {w}</div>
                ))}
              </div>
            )}
          </form>
        </div>
      </Can>

      {/* ── Range filter ───────────────────────────────────────────── */}
      <div className="section-card" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 12, padding: 16 }}>
        <div className="form-group" style={{ marginBottom: 0 }}><label>{tr('common.from')}</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} /></div>
        <div className="form-group" style={{ marginBottom: 0 }}><label>{tr('common.to')}</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} /></div>
      </div>

      {/* ── Transfers table ────────────────────────────────────────── */}
      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>{tr('common.date')}</th><th>{tr('tankTransfer.ref')}</th><th>{tr('common.from')}</th><th>{tr('common.to')}</th>
                <th style={{ textAlign: 'right' }}>{tr('tankTransfer.qtyL')}</th><th>{tr('common.remarks')}</th><th></th>
              </tr>
            </thead>
            <tbody>
              {(data || []).length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                    <div style={{ fontSize: 28, marginBottom: 6 }}>🔁</div>
                    {tr('tankTransfer.noTransfers')}
                    <div style={{ fontSize: 12, marginTop: 4 }}>{tr('tankTransfer.noTransfersHint')}</div>
                  </td>
                </tr>
              )}
              {(data || []).map(r => (
                <tr key={r.id}>
                  <td>{fmtDate(r.transfer_date)}</td>
                  <td style={{ fontFamily: 'monospace', color: 'var(--primary)' }}>TR-{r.id}</td>
                  <td>{r.from_tank_no || tankName(r.from_tank_id)}</td>
                  <td>{r.to_tank_no || tankName(r.to_tank_id)}</td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{fmtNum(r.qty)}</td>
                  <td>{r.remarks || '—'}</td>
                  <td>
                    <Can module="stock_ledger" action="delete">
                      <button className="btn btn-sm btn-danger" onClick={() => remove(r.id)}>{tr('common.delete')}</button>
                    </Can>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Inline KPI tile styles — matches the summary strips used elsewhere in the app.
const kpi = {
  tile: {
    flex: 1, minWidth: 150, padding: '12px 16px',
    background: 'var(--surface, #fff)', border: '1px solid var(--border, #e2e8f0)',
    borderRadius: 10,
  },
  label: {
    fontSize: 11, fontWeight: 700, color: 'var(--text-muted, #64748b)',
    textTransform: 'uppercase', letterSpacing: 0.5,
  },
  value: {
    fontSize: 22, fontWeight: 800, color: 'var(--text-primary, #0f172a)',
    fontVariant: 'tabular-nums', marginTop: 2,
  },
};
